/**
 * transferCard — pure logic behind the branch-transfer card (owner 2026-09-19:
 * "the details are all over the place and don't give proper context").
 *
 * Three questions the card answers, each a pure function of the transfer doc:
 *   buildTimeline  — what happened, when, and who did it (dated, named, and
 *                    shaped by the path THIS transfer actually took)
 *   whoseMove      — who has to act now, and since when
 *   unitSummary    — what is in it, in the units the warehouse counts in
 *
 * No React, no dates formatted here — callers format `at` for display.
 */

const VARIANCE_STATES = ['received_pending', 'disputed'];

/** Steps: { key, label, state: 'done'|'now'|'todo'|'problem', at, by, note } */
export function buildTimeline(o) {
  if (!o) return [];
  const s = o.status;
  const steps = [];
  const add = (key, label, state, at, by, note) =>
    steps.push({ key, label, state, at: at || null, by: by || '', note: note || '' });

  if (o.source_request_id) {
    add('requested', 'Requested', 'done', o.source_request_at, o.source_request_by_name);
  }
  add('created', 'Created', 'done', o.created_at, o.created_by_name);

  if (s === 'cancelled') {
    add('cancelled', 'Cancelled', 'problem', o.cancelled_at, o.cancelled_by_name);
    return steps;
  }

  // Approval only exists on the path that went through it.
  if (o.approved_at) add('approved', 'Approved', 'done', o.approved_at, o.approved_by_name);
  else if (s === 'pending_approval') add('approval', 'Approval', 'now', null, '', 'Waiting on admin');
  else if (s === 'returned') add('returned', 'Returned', 'problem', o.rejected_at, o.rejected_by_name);

  const sent = !!o.sent_at && !['draft', 'pending_approval', 'returned'].includes(s);
  add('sent', 'Sent', sent ? 'done' : 'todo', sent ? o.sent_at : null, sent ? o.sent_by_name : '');

  // A count that differed from what was sent takes the variance path:
  // Counted → (Disputed → re-count) → Accepted. An exact count is one moment.
  const variancePath = !!o.pending_receipt_at || VARIANCE_STATES.includes(s) || !!o.accepted_at;
  if (variancePath) {
    const recounts = (o.dispute_history || []).length;
    add('counted', 'Counted', o.pending_receipt_at ? 'done' : 'todo',
      o.pending_receipt_at, o.pending_receipt_by_name,
      recounts ? `Re-count ${recounts}` : '');
    if (s === 'disputed') {
      add('disputed', 'Disputed', 'problem', o.disputed_at, o.disputed_by_name);
      add('recount', 'Re-count', 'todo');
    }
    add('accepted', 'Accepted', o.accepted_at ? 'done' : 'todo', o.accepted_at, o.accepted_by_name);
  } else if (s === 'sent_to_terminal') {
    add('terminal', 'On terminal', 'todo', null, '', 'Being checked');
    add('received', 'Received', 'todo');
  } else {
    add('received', 'Received', s === 'received' ? 'done' : 'todo',
      s === 'received' ? o.received_at : null, s === 'received' ? o.received_by_name : '');
  }

  // A shortage is not closed by the accept: it stays open on its discrepancy
  // ticket until the source settles it (found / never loaded / written off).
  if (o.has_shortage && (s === 'received' || variancePath)) {
    const settled = !!o.variance_resolved;
    add('settled', 'Shortage settled', settled ? 'done' : (s === 'received' ? 'now' : 'todo'),
      settled ? (o.variance_resolved_at || null) : null, '',
      settled ? '' : (o.incident_ticket_number ? `Ticket ${o.incident_ticket_number}` : 'Ticket open'));
  }
  // The first open step is where the transfer IS — unless it is finished.
  if (s !== 'received') {
    const next = steps.find(st => st.state === 'todo');
    if (next && !steps.some(st => st.state === 'now')) next.state = 'now';
  }
  return steps;
}

/** Who acts next. `me` = { isSource, isDest, canApprove }. Null when nobody must. */
export function whoseMove(o, me = {}, names = {}) {
  if (!o) return null;
  const from = names.from || 'the sending branch';
  const to = names.to || 'the receiving branch';
  const mine = (text, since) => ({ mine: true, text, since: since || null });
  const theirs = (text, since) => ({ mine: false, text, since: since || null });
  switch (o.status) {
    case 'draft':
      return me.isSource ? mine('Review the prices and send.', o.created_at)
        : theirs(`Waiting on ${from} to send.`, o.created_at);
    case 'pending_approval':
      return me.canApprove ? mine('Set retail prices and release the order.', o.created_at)
        : theirs('Waiting on admin approval.', o.created_at);
    case 'returned':
      return me.isSource ? mine('Fix what was flagged and resubmit.', o.rejected_at)
        : theirs(`Waiting on ${from} to fix and resubmit.`, o.rejected_at);
    case 'sent':
      return me.isDest ? mine('Count the goods and receive.', o.sent_at)
        : theirs(`Waiting on ${to} to count and receive.`, o.sent_at);
    case 'sent_to_terminal':
      return theirs(`Being checked on a terminal at ${to}.`, o.sent_at);
    case 'received_pending':
      return me.isSource ? mine('Accept the counted quantities, or dispute them.', o.pending_receipt_at)
        : theirs(`Waiting on ${from} to accept the count.`, o.pending_receipt_at);
    case 'disputed':
      return me.isDest ? mine('Re-count and resubmit.', o.disputed_at)
        : theirs(`Waiting on ${to} to re-count.`, o.disputed_at);
    default:
      return null;
  }
}

/**
 * List order (owner 2026-09-19). Priority decides the GROUP, a date decides
 * the order inside it:
 *   1 Discrepancies            oldest first   unresolved stock + money (variance
 *                                             waiting on a decision, disputed) —
 *                                             NOT an accepted shortage: that is
 *                                             Completed, its ticket carries it
 *   2 Drafts / returned        NEWEST first   the fresh draft is the truck being
 *                                             loaded today; an old one is abandoned
 *   3 Review and approve       oldest first   newer ones must not bury a stuck one
 *   4 Ready to receive         oldest first   longest in transit = most likely
 *                                             already on the shelf, selling off a
 *                                             book that never received it; and
 *                                             receiving in order keeps LP/MA honest
 *   5 Waiting on the other side oldest first  same docs as 2/4 seen from the branch
 *                                             that has nothing to do
 *   6 Completed                newest first
 *   7 Cancelled                newest first
 * `me` = { isSource, isDest } for the viewing branch; both false ("all
 * branches") falls back to the plain priority order.
 */
export function sortGroup(o, me = {}) {
  const senderOnly = !!me.isSource && !me.isDest;
  const receiverOnly = !!me.isDest && !me.isSource;
  switch (o.status) {
    case 'received_pending':
    case 'disputed':
      return 1;
    case 'draft':
    case 'returned':
      return receiverOnly ? 5 : 2;
    case 'pending_approval':
      return 3;
    case 'sent':
    case 'sent_to_terminal':
      return senderOnly ? 5 : 4;
    case 'received':
      // Accepted = done, shortage or not (owner 2026-09-21). The 09-19 rule
      // pinned an accepted shortage with an open ticket in group 1 for the
      // source branch; tickets stay open for months, so May transfers sat
      // above today's work forever. The TICKET owns the follow-up (Incident
      // Tickets); the card still says "Shortage settled — Ticket open".
      return 6;
    case 'cancelled':
      return 7;
    default:
      return 6;
  }
}

const NEWEST_FIRST_GROUPS = new Set([2, 6, 7]);

function sortDate(o) {
  const pick = {
    received_pending: o.pending_receipt_at, disputed: o.disputed_at,
    draft: o.created_at, returned: o.rejected_at, pending_approval: o.created_at,
    sent: o.sent_at, sent_to_terminal: o.sent_at,
    received: o.received_at, cancelled: o.cancelled_at,
  }[o.status];
  const t = new Date(pick || o.updated_at || o.created_at || 0).getTime();
  return Number.isNaN(t) ? 0 : t;
}

/** Returns a NEW array. `meOf(order)` → { isSource, isDest }. */
export function sortTransfers(orders, meOf = () => ({})) {
  return (orders || [])
    .map(o => ({ o, g: sortGroup(o, meOf(o)), t: sortDate(o) }))
    .sort((a, b) => a.g - b.g
      || (NEWEST_FIRST_GROUPS.has(a.g) ? b.t - a.t : a.t - b.t)
      || String(a.o.order_number || '').localeCompare(String(b.o.order_number || '')))
    .map(x => x.o);
}

/** Age of a wait. level: null (<1 day) | 'warn' (1–2 days) | 'late' (3+ days). */
export function waitAge(since, now = Date.now()) {
  const t = since ? new Date(since).getTime() : NaN;
  if (Number.isNaN(t)) return null;
  const hours = Math.max(0, (now - t) / 3600000);
  const days = Math.floor(hours / 24);
  if (days < 1) return { days: 0, label: '', level: null };
  return { days, label: `Waiting ${days} ${days === 1 ? 'day' : 'days'}`, level: days >= 3 ? 'late' : 'warn' };
}

/** Whole days from the first step to completion; null until finished. */
export function doorToDoorDays(o) {
  if (!o || o.status !== 'received' || !o.received_at) return null;
  const start = new Date(o.source_request_at || o.created_at).getTime();
  const end = new Date(o.received_at).getTime();
  if (Number.isNaN(start) || Number.isNaN(end) || end < start) return null;
  return Math.round((end - start) / 86400000);
}

const trimQty = (n) => parseFloat(Number(n).toFixed(3)).toString();
const unitOf = (i) => String(i.unit || '').trim().toLowerCase() || 'unit';

function tally(items) {
  const m = new Map();
  for (const i of items) {
    const q = parseFloat(i.qty) || 0;
    if (q <= 0) continue;
    m.set(unitOf(i), (m.get(unitOf(i)) || 0) + q);
  }
  // Old documents keep the spelling they were printed with, so one transfer
  // can carry "bag" on one line and "bags" on another. For DISPLAY a plural
  // joins its singular when both appear (same rule as the catalog cleanup).
  for (const unit of [...m.keys()]) {
    const single = unit.length > 2 && unit.endsWith('s') && !unit.endsWith('ss') ? unit.slice(0, -1) : null;
    if (single && m.has(single)) { m.set(single, m.get(single) + m.get(unit)); m.delete(unit); }
  }
  return [...m.entries()].sort((a, b) => b[1] - a[1]).map(([unit, qty]) => ({ unit, qty, text: `${trimQty(qty)} ${unit}` }));
}

/** totals: by unit across the whole transfer. categories: the same per
 *  category, biggest first — "Veterinary: 20 l, 20 bot". */
export function unitSummary(items) {
  const list = (items || []).filter(i => (parseFloat(i.qty) || 0) > 0);
  const byCat = new Map();
  for (const i of list) {
    const cat = String(i.category || '').trim() || 'Uncategorized';
    if (!byCat.has(cat)) byCat.set(cat, []);
    byCat.get(cat).push(i);
  }
  const categories = [...byCat.entries()]
    .map(([name, rows]) => ({ name, lines: rows.length, units: tally(rows) }))
    .sort((a, b) => b.lines - a.lines || a.name.localeCompare(b.name));
  return { lines: list.length, totals: tally(list), categories };
}
